import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from './config';
import QRCode from 'qrcode';
import { BrowserQRCodeReader } from '@zxing/browser';
import '../css/Reward.css';

const getAvatarSource = avatarUrl => {
  if (!avatarUrl) return '';
  if (/^https?:\/\//i.test(avatarUrl) || avatarUrl.startsWith('data:')) return avatarUrl;
  return `${API_BASE}${avatarUrl.startsWith('/') ? '' : '/'}${avatarUrl}`;
};

function Reward() {
  const navigate = useNavigate();

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  }, []);

  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';

const [data, setData] = useState({
  balance: 0,

  campaigns: [],

  missions: [],

  transactions: [],

  redemptions: [],

  coupons: [],

  profile: null,

  friends: [],

  progress: {}
});

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [redeem, setRedeem] = useState(null);
  const [qrImage, setQrImage] = useState('');
  const [memberTab, setMemberTab] = useState('missions');
  const [referralInvite, setReferralInvite] = useState(null);
  const [referralQr, setReferralQr] = useState('');
  const [referralScannerOpen, setReferralScannerOpen] = useState(false);
  const referralVideoRef = useRef(null);
  const [shirtQr, setShirtQr] = useState('');
  const [adminScannerOpen, setAdminScannerOpen] = useState(false);
  const adminVideoRef = useRef(null);

  // สำหรับ Admin แก้ Point
  const [pointDraft, setPointDraft] = useState({});
  const [savingId, setSavingId] = useState(null);

  const authFetch = useCallback(async (path, options = {}) => {
    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        ...(options.headers || {})
      }
    });

    const result = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(result.message || 'ทำรายการไม่สำเร็จ');
    }

    return result;
  }, []);

  const loadRewards = useCallback(async () => {
    try {
      setLoading(true);
      setMessage('');

      // โหลดข้อมูล Reward ของ User
      const me = await authFetch('/api/rewards/me');

      // ถ้าเป็น Admin ให้โหลด Campaign ทั้งหมด
      if (isAdmin) {
        const adminResult = await authFetch('/api/rewards/admin/campaigns');

        const allCampaigns = Array.isArray(adminResult)
          ? adminResult
          : adminResult.campaigns || [];

        setData({
          ...me,
          campaigns: allCampaigns
        });

        // เตรียมค่า Point สำหรับแก้ไข
        const drafts = {};

        allCampaigns.forEach(item => {
          drafts[item.id] = Number(item.points_cost || 0);
        });

        setPointDraft(drafts);
      } else {
        setData(me);
      }
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, isAdmin]);

  const confirmAdminScan = useCallback(async scannedValue => {
    const scanned = String(scannedValue || '').trim();
    const playerMatch = scanned.match(/(?:user_id|userId|playerId|player_id|id)[=/:](\d+)/i)
      || scanned.match(/^PLAYER:(\d+)$/i);
    const result = playerMatch
      ? await authFetch('/api/rewards/admin/missions/shirt-confirm', {
          method: 'POST', body: JSON.stringify({ userId: playerMatch[1] })
        })
      : await authFetch(`/api/rewards/admin/redemptions/${encodeURIComponent(scanned)}/confirm`, { method: 'POST' });
    setMessage(`✅ ${result.message}`);
    await loadRewards();
  }, [authFetch, loadRewards]);

  useEffect(() => {
    if (!user?.user_id) {
      const returnTo = `/rewards${window.location.search || ''}`;
      navigate(`/?redirect=${encodeURIComponent(returnTo)}`, { replace: true });
      return;
    }

    loadRewards();
  }, [loadRewards, navigate, user?.user_id]);

  useEffect(() => {
    if (!user?.user_id) return;
    const code = new URLSearchParams(window.location.search).get('ref');
    if (!code) return;
    authFetch(`/api/rewards/referrals/${encodeURIComponent(code)}`)
      .then(result => setReferralInvite({ mode: 'connect', code, ...result.referral }))
      .catch(error => setMessage(error.message));
  }, [authFetch, user?.user_id]);

  useEffect(() => {
    if (!referralScannerOpen || !referralVideoRef.current) return undefined;
    const reader = new BrowserQRCodeReader();
    let active = true;
    let controlsRef = null;
    reader.decodeFromConstraints(
      { video: { facingMode: { ideal: 'environment' } } },
      referralVideoRef.current,
      (result, error, controls) => {
        controlsRef = controls;
        if (!active || !result) return;
        try {
          const scanned = String(result.getText() || '').trim();
          const url = new URL(scanned, window.location.origin);
          const code = url.searchParams.get('ref');
          if (!code) throw new Error('QR นี้ไม่ใช่ QR ชวนเพื่อน');
          setReferralScannerOpen(false);
          controls.stop();
          authFetch(`/api/rewards/referrals/${encodeURIComponent(code)}`)
            .then(response => setReferralInvite({ mode: 'connect', code, ...response.referral }))
            .catch(scanError => setMessage(scanError.message));
        } catch (scanError) {
          setMessage(scanError.message || 'QR ไม่ถูกต้อง');
        }
      }
    ).catch(error => { setMessage(error.message || 'เปิดกล้องไม่สำเร็จ'); setReferralScannerOpen(false); });
    return () => { active = false; controlsRef?.stop(); };
  }, [authFetch, referralScannerOpen]);

  useEffect(() => {
    if (!adminScannerOpen || !adminVideoRef.current) return undefined;
    const reader = new BrowserQRCodeReader();
    let active = true;
    let controlsRef = null;
    reader.decodeFromConstraints(
      { video: { facingMode: { ideal: 'environment' } } },
      adminVideoRef.current,
      (result, error, controls) => {
        controlsRef = controls;
        if (!active || !result) return;
        active = false;
        controls.stop();
        setAdminScannerOpen(false);
        confirmAdminScan(result.getText()).catch(scanError => setMessage(scanError.message));
      }
    ).catch(error => {
      setMessage(error.message || 'เปิดกล้องไม่สำเร็จ');
      setAdminScannerOpen(false);
    });
    return () => { active = false; controlsRef?.stop(); };
  }, [adminScannerOpen, confirmAdminScan]);

  useEffect(() => {
    const code = redeem?.redeemCode;
    if (!code) { setQrImage(''); return undefined; }
    let active = true;
    QRCode.toDataURL(code, { width: 360, margin: 2, errorCorrectionLevel: 'M' })
      .then(image => { if (active) setQrImage(image); })
      .catch(() => { if (active) setQrImage(''); });
    return () => { active = false; };
  }, [redeem?.redeemCode]);

  const claimMission = async mission => {
    try {
      setMessage('');
      const result = await authFetch(`/api/rewards/missions/${mission.id}/claim`, { method: 'POST' });
      setMessage(`✅ ${result.message}`);
      await loadRewards();
    } catch (error) { setMessage(error.message); }
  };

  const activateCoupon = async coupon => {
    try {
      setMessage('');
      const result = await authFetch(`/api/rewards/coupons/${coupon.id}/prepare`, { method: 'POST' });
      setRedeem({ title: result.coupon.title, redeemCode: result.coupon.coupon_code, rewardLabel: result.coupon.reward_label });
      await loadRewards();
    } catch (error) { setMessage(error.message); }
  };

  const activateRedemption = async redemption => {
    try {
      setMessage('');
      const result = await authFetch(`/api/rewards/redemptions/${redemption.id}/prepare`, { method: 'POST' });
      setRedeem({ title: result.redemption.title, redeemCode: result.redemption.redeem_code, rewardLabel: result.redemption.title });
      await loadRewards();
    } catch (error) { setMessage(error.message); }
  };

  const openReferralQr = async mission => {
    try {
      setMessage('');
      const result = await authFetch('/api/rewards/referrals/code', {
        method: 'POST',
        body: JSON.stringify({ missionId: mission.id })
      });
      const url = `${window.location.origin}/rewards?ref=${encodeURIComponent(result.code)}`;
      const image = await QRCode.toDataURL(url, { width: 360, margin: 2, errorCorrectionLevel: 'M' });
      setReferralQr(image);
      setReferralInvite({ mode: 'share', title: mission.title, rewardLabel: mission.reward_text, url });
    } catch (error) { setMessage(error.message); }
  };

  const connectReferral = async () => {
    try {
      setMessage('');
      const result = await authFetch(`/api/rewards/referrals/${encodeURIComponent(referralInvite.code)}/connect`, { method: 'POST' });
      setMessage(`✅ ${result.message}`);
      setReferralInvite(null);
      window.history.replaceState({}, '', '/rewards');
      await loadRewards();
    } catch (error) { setMessage(error.message); }
  };

  const openShirtQr = async () => {
    const payload = `${window.location.origin}/profile?playerId=${encodeURIComponent(user.user_id)}`;
    const image = await QRCode.toDataURL(payload, { width: 360, margin: 2, errorCorrectionLevel: 'M' });
    setShirtQr(image);
  };

  // =========================
  // MEMBER REDEEM
  // =========================

  const redeemReward = async campaign => {
    if (
      !window.confirm(
        `แลก “${campaign.title}” ใช้ ${campaign.points_cost} Point ใช่หรือไม่?`
      )
    ) {
      return;
    }

    try {
      setMessage('');

      const result = await authFetch(
        `/api/rewards/redeem/${campaign.id}`,
        {
          method: 'POST'
        }
      );

      await loadRewards();
      setMemberTab('coupons');
      setMessage(`✅ ${result.message || 'แลกรางวัลเรียบร้อย คูปองถูกเพิ่มแล้ว'}`);
    } catch (error) {
      setMessage(error.message);
    }
  };

  // =========================
  // ADMIN CHANGE POINT
  // =========================

  const changePoint = (campaignId, value) => {
    setPointDraft(prev => ({
      ...prev,
      [campaignId]: value
    }));
  };

  const decreasePoint = campaign => {
    const current = Number(
      pointDraft[campaign.id] ?? campaign.points_cost ?? 0
    );

    setPointDraft(prev => ({
      ...prev,
      [campaign.id]: Math.max(0, current - 5)
    }));
  };

  const increasePoint = campaign => {
    const current = Number(
      pointDraft[campaign.id] ?? campaign.points_cost ?? 0
    );

    setPointDraft(prev => ({
      ...prev,
      [campaign.id]: current + 5
    }));
  };

  const savePoint = async campaign => {
    const newPoint = Number(pointDraft[campaign.id]);

    if (!Number.isFinite(newPoint) || newPoint < 0) {
      setMessage('Point ต้องเป็นตัวเลขตั้งแต่ 0 ขึ้นไป');
      return;
    }

    try {
      setSavingId(campaign.id);
      setMessage('');

      await authFetch(
        `/api/rewards/admin/campaigns/${campaign.id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            points_cost: Math.floor(newPoint)
          })
        }
      );

      // Update หน้าโดยไม่ต้อง Reload
      setData(prev => ({
        ...prev,
        campaigns: prev.campaigns.map(item =>
          item.id === campaign.id
            ? {
                ...item,
                points_cost: Math.floor(newPoint)
              }
            : item
        )
      }));

      setPointDraft(prev => ({
        ...prev,
        [campaign.id]: Math.floor(newPoint)
      }));

      setMessage(
        `✅ ${campaign.title} ใช้ ${Math.floor(newPoint)} Point แล้ว`
      );
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSavingId(null);
    }
  };

  // =========================
  // PROGRESS
  // =========================

  return (
    <main className="reward-page">

      {/* ================= HERO ================= */}

      <header className="reward-hero">
        <button
          type="button"
          onClick={() => navigate('/profile')}
        >
          ← โปรไฟล์
        </button>

        <div className="reward-profile-summary">
          <div className="reward-profile-avatar">
            {getAvatarSource(data.profile?.avatar_url || user?.avatar_url) ? (
              <img src={getAvatarSource(data.profile?.avatar_url || user?.avatar_url)} alt={data.profile?.name || user?.name || 'ผู้เล่น'} />
            ) : (
              <span>{String(data.profile?.name || user?.name || 'U').trim().charAt(0).toUpperCase()}</span>
            )}
          </div>
          <div>
          <small>
            {isAdmin ? 'ADMIN REWARDS' : 'MY REWARDS'}
          </small>

          <h1>{data.profile?.name || user?.name || 'Player'}</h1>

          <p>
            🎁 Reward • Rank {data.profile?.level_rank || user?.level_rank || '-'}
          </p>
          </div>
        </div>

        <div className="reward-hero-side">
          <strong>{Number(data.balance || 0).toLocaleString()} <span>Point</span></strong>
          <section className="reward-friends">
            <header><b>My Friend</b><span>{(data.friends || []).length}</span></header>
            <div>
              {(data.friends || []).slice(0, 4).map(friend => (
                <article key={friend.user_id} title={friend.relation_type === 'INVITED' ? 'เพื่อนที่คุณชวน' : 'เพื่อนที่ชวนคุณ'}>
                  <i>
                    {getAvatarSource(friend.avatar_url)
                      ? <img src={getAvatarSource(friend.avatar_url)} alt={friend.name} />
                      : String(friend.name || 'F').charAt(0)}
                  </i>
                  <span><b>{friend.name}</b><small>Best Friend ❤️</small></span>
                </article>
              ))}
              {!(data.friends || []).length && <small className="reward-no-friend">ชวนเพื่อนคนแรกของคุณ 🤍</small>}
            </div>
          </section>
        </div>
      </header>

      {/* ================= ADMIN BAR ================= */}

      {isAdmin && (
        <div className="reward-admin-bar">
          <div>
            <strong>🛡 Admin Mode</strong>

            <small>
              คุณกำลังเห็นโปรโมชั่นและของรางวัลทั้งหมด
            </small>
          </div>

          <button
            type="button"
            onClick={() => navigate('/admin/rewards')}
          >
            จัดการ Reward
          </button>
        </div>
      )}

      {isAdmin && (
        <section className="reward-admin-quick-scan">
          <div>
            <span>📷</span>
            <div>
              <strong>ยืนยันรับของรางวัล</strong>
              <small>สแกนได้ทั้ง QR คูปองและ QR เสื้อก๊วน ระบบจะยืนยันให้ทันที</small>
            </div>
          </div>
          <button type="button" onClick={() => setAdminScannerOpen(true)}>
            📷 สแกน QR เพื่อยืนยันสิทธิ์
          </button>
        </section>
      )}

      {/* ================= MESSAGE ================= */}

      {message && (
        <div
          className={
            message.startsWith('✅')
              ? 'reward-message success'
              : 'reward-message'
          }
        >
          {message.startsWith('✅') ? '' : '⚠️ '}
          {message}
        </div>
      )}

      {/* ================= MISSIONS ================= */}

      {!isAdmin && (
        <nav className="reward-member-tabs" aria-label="เมนูรางวัล">
          <button
            type="button"
            className={memberTab === 'missions' ? 'active' : ''}
            onClick={() => setMemberTab('missions')}
          >
            🎯 ภารกิจของฉัน
          </button>
          <button
            type="button"
            className={memberTab === 'exchange' ? 'active' : ''}
            onClick={() => setMemberTab('exchange')}
          >
            🎁 แลกของรางวัล
          </button>
          <button
            type="button"
            className={memberTab === 'coupons' ? 'active' : ''}
            onClick={() => setMemberTab('coupons')}
          >
            🎟 คูปองของฉัน
            <b>{(data.coupons || []).filter(item => item.status !== 'REDEEMED').length + (data.redemptions || []).filter(item => item.status !== 'REDEEMED').length}</b>
          </button>
        </nav>
      )}

      {!isAdmin && memberTab === 'missions' && (
      <section className="reward-missions">

  <h2>ภารกิจของฉัน</h2>

  {data.missions?.map(mission => {

    const current = mission.claimable
      ? Number(mission.target || 1)
      : Number(mission.current || 0);

    const target =
      Math.max(
        1,
        Number(mission.target || 1)
      );

    const percent =
      Math.min(
        100,
        (current / target) * 100
      );

    const isInstant = mission.claim_mode === 'INSTANT';
    const hasClaimed = Number(mission.claimedRounds || 0) > 0;

    return (
      <article key={mission.id}>

        <span>
          {mission.icon || '🎯'}
          {' '}
          {mission.title}

          {!isInstant && (
            <>
              {' '}
              ครบ {target} ครั้ง
            </>
          )}
        </span>

        <strong>
          {isInstant
            ? (hasClaimed ? 'รับแล้ว' : 'พร้อมรับ')
            : `${current}/${target}`}
        </strong>

        <i>
          <b
            style={{
              width: isInstant
                ? (mission.claimable || hasClaimed ? '100%' : '0%')
                : `${percent}%`
            }}
          />
        </i>

        <small>
          {mission.reward_text ||
            'รับรางวัลเมื่อทำภารกิจสำเร็จ'}
        </small>

        {mission.mission_type === 'ADMIN_CONFIRM' ? (
          <button type="button" className="reward-claim-button is-ready" onClick={openShirtQr}>
            👕 แสดง QR ให้ Admin เพื่อรับชิป
          </button>
        ) : mission.mission_type === 'REFERRAL' ? (
          <button type="button" className="reward-claim-button is-ready" onClick={() => openReferralQr(mission)}>📤 เปิด QR ของฉัน</button>
        ) : (
          <button
            type="button"
            className={`reward-claim-button${mission.claimable ? ' is-ready' : ''}`}
            disabled={!mission.claimable}
            onClick={() => claimMission(mission)}
          >
            {mission.claimable
              ? (mission.reward_delivery === 'POINT' ? `✓ รับ ${Number(mission.reward_points || 0)} Point` : '✓ รับรางวัล')
              : (hasClaimed ? 'รับแล้ว' : mission.claim_action === 'ADMIN' ? 'รับที่ Admin' : 'ยังไม่ครบ')}
          </button>
        )}

      </article>
    );
  })}

  {!data.missions?.length && (
    <div className="reward-empty">
      ยังไม่มีภารกิจ
    </div>
  )}

</section>
      )}

      {!isAdmin && memberTab === 'coupons' && (
        <section className="reward-coupons">
          <header><div><h2>คูปองของฉัน</h2><p>กดใช้คูปองเพื่อสร้าง QR ให้ Admin สแกน</p></div><b>{(data.coupons || []).filter(item => item.status !== 'REDEEMED').length + (data.redemptions || []).filter(item => item.status !== 'REDEEMED').length} ใบ</b></header>
          <div className="reward-coupon-grid">
            {(data.redemptions || []).map(coupon => (
              <article key={`reward-${coupon.id}`} className={`status-${String(coupon.status).toLowerCase()}`}>
                <span>🎁</span><div><small>{coupon.status === 'REDEEMED' ? 'ใช้แล้ว' : 'พร้อมใช้'}</small><h3>{coupon.title}</h3><p>แลกด้วย {Number(coupon.points_cost || 0).toLocaleString()} Point</p></div>
                <button type="button" disabled={coupon.status === 'REDEEMED'} onClick={() => activateRedemption(coupon)}>{coupon.status === 'REDEEMED' ? 'ใช้แล้ว' : 'ใช้คูปอง'}</button>
              </article>
            ))}
            {(data.coupons || []).map(coupon => (
              <article key={coupon.id} className={`status-${String(coupon.status).toLowerCase()}`}>
                <span>🎟</span><div><small>{coupon.status === 'REDEEMED' ? 'ใช้แล้ว' : 'พร้อมใช้'}</small><h3>{coupon.reward_label}</h3><p>{coupon.title} • รอบที่ {coupon.cycle_no}</p></div>
                <button type="button" disabled={coupon.status === 'REDEEMED'} onClick={() => activateCoupon(coupon)}>{coupon.status === 'REDEEMED' ? 'ใช้แล้ว' : 'ใช้คูปอง'}</button>
              </article>
            ))}
          </div>
          {(data.coupons || []).length === 0 && (data.redemptions || []).length === 0 && <div className="reward-empty">ยังไม่มีคูปอง สามารถทำภารกิจหรือแลกของรางวัลเพื่อรับคูปองได้</div>}
        </section>
      )}

      {/* ================= STORE ================= */}

      <section className={`reward-store${!isAdmin && memberTab !== 'exchange' ? ' reward-tab-hidden' : ''}`}>

        <header>
          <div>
            <h2>
              {isAdmin
                ? 'โปรโมชั่น / ของรางวัลทั้งหมด'
                : 'ของรางวัลและโปรโมชัน'}
            </h2>

            <p>
              {isAdmin
                ? 'สามารถเพิ่มหรือลด Point ของแต่ละรายการได้'
                : 'เลือกรางวัลแล้วนำรหัสไปให้ Admin ยืนยัน'}
            </p>
          </div>

          <button
            type="button"
            onClick={loadRewards}
          >
            ↻ อัปเดต
          </button>
        </header>

        {loading ? (
          <div className="reward-empty">
            กำลังโหลด...
          </div>
        ) : data.campaigns?.length === 0 ? (
          <div className="reward-empty">
            ยังไม่มีของรางวัล
          </div>
        ) : (
          <div className="reward-grid">

            {data.campaigns.map(item => {
              const inactive =
                Number(item.is_active) === 0;

              const currentPoint =
                Number(pointDraft[item.id] ?? 0);

              const originalPoint =
                Number(item.points_cost || 0);

              const pointChanged =
                currentPoint !== originalPoint;

              return (
                <article
                  key={item.id}
                  className={
                    inactive
                      ? 'reward-card-inactive'
                      : ''
                  }
                >

                  {/* IMAGE */}

                  <div className="reward-art">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.title || 'Reward'}
                      />
                    ) : (
                      '🎁'
                    )}
                  </div>

                  {/* LABEL */}

                  <div className="reward-card-labels">

                    <small>
                      {item.reward_type === 'PROMOTION'
                        ? 'PROMOTION'
                        : 'REWARD'}
                    </small>

                    {isAdmin && (
                      <span
                        className={
                          inactive
                            ? 'reward-status inactive'
                            : 'reward-status active'
                        }
                      >
                        {inactive
                          ? 'ปิดใช้งาน'
                          : 'เปิดใช้งาน'}
                      </span>
                    )}

                  </div>

                  <h3>{item.title}</h3>

                  <p>
                    {item.description ||
                      'สิทธิพิเศษสำหรับสมาชิกก๊วน'}
                  </p>

                  {/* ================= ADMIN ================= */}

                  {isAdmin ? (
                    <div className="reward-admin-point">

                      <label>
                        Point ที่ใช้แลก
                      </label>

                      <div className="reward-point-control">

                        <button
                          type="button"
                          onClick={() =>
                            decreasePoint(item)
                          }
                        >
                          −
                        </button>

                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={
                            pointDraft[item.id] ?? 0
                          }
                          onChange={e =>
                            changePoint(
                              item.id,
                              e.target.value
                            )
                          }
                        />

                        <button
                          type="button"
                          onClick={() =>
                            increasePoint(item)
                          }
                        >
                          +
                        </button>

                      </div>

                      <div className="reward-point-preview">

                        <span>
                          Point ปัจจุบัน
                        </span>

                        <strong>
                          {originalPoint.toLocaleString()}
                        </strong>

                        {pointChanged && (
                          <>
                            <span>→</span>

                            <strong>
                              {currentPoint.toLocaleString()}
                            </strong>
                          </>
                        )}

                      </div>

                      <button
                        className="reward-save-point"
                        type="button"
                        disabled={
                          savingId === item.id ||
                          !pointChanged
                        }
                        onClick={() =>
                          savePoint(item)
                        }
                      >
                        {savingId === item.id
                          ? 'กำลังบันทึก...'
                          : pointChanged
                          ? '💾 บันทึก Point'
                          : '✓ Point ปัจจุบัน'}
                      </button>

                    </div>
                  ) : (

                    /* ================= MEMBER ================= */

                    <footer>

                      <strong>
                        {Number(
                          item.points_cost || 0
                        ).toLocaleString()}
                        {' '}
                        Point
                      </strong>

                      <button
                        type="button"
                        disabled={
                          Number(data.balance || 0) <
                          Number(item.points_cost || 0)
                        }
                        onClick={() =>
                          redeemReward(item)
                        }
                      >
                        {Number(data.balance || 0) <
                        Number(item.points_cost || 0)
                          ? 'Point ไม่พอ'
                          : 'แลกรางวัล'}
                      </button>

                    </footer>
                  )}

                </article>
              );
            })}

          </div>
        )}
      </section>

      {/* ================= HISTORY ================= */}

      {!isAdmin && (
        <section className="reward-history">

          <h2>ประวัติล่าสุด</h2>

          {(data.transactions || [])
            .slice(0, 8)
            .map(item => (
              <div key={item.id}>

                <span>
                  {item.note || item.event_type}
                </span>

                <strong
                  className={
                    Number(item.amount) >= 0
                      ? 'plus'
                      : 'minus'
                  }
                >
                  {Number(item.amount) > 0
                    ? '+'
                    : ''}

                  {item.amount}
                </strong>

              </div>
            ))}

          {(data.transactions || []).length === 0 && (
            <div className="reward-empty">
              ยังไม่มีประวัติ Point
            </div>
          )}

        </section>
      )}

      {/* ================= REDEEM MODAL ================= */}

      {shirtQr && (
        <div className="reward-code-backdrop" role="dialog" aria-modal="true">
          <div className="reward-code-modal reward-referral-modal">
            <button type="button" onClick={() => setShirtQr('')}>×</button>
            <span>SHIRT CHECK</span>
            <h2>ใส่เสื้อก๊วน รับฟรี 1 ชิป</h2>
            <div className="reward-code-mark"><img src={shirtQr} alt="QR ยืนยันใส่เสื้อก๊วน" /></div>
            <p>แสดง QR นี้ให้ Admin ตรวจเสื้อและสแกน<br />เมื่อสแกนสำเร็จถือว่ามอบชิปแล้วทันที</p>
          </div>
        </div>
      )}

      {!isAdmin && (
        <nav className="reward-bottom-scan" aria-label="สแกน QR ชวนเพื่อน">
          <button type="button" onClick={() => setReferralScannerOpen(true)}>
            <span>▣</span>
            <b>สแกน QR </b>
          </button>
        </nav>
      )}

      {referralScannerOpen && (
        <div className="reward-code-backdrop" role="dialog" aria-modal="true">
          <div className="reward-code-modal reward-admin-scanner">
            <button type="button" onClick={() => setReferralScannerOpen(false)}>×</button>
            <h2>สแกน QR ชวนเพื่อน</h2>
            <video ref={referralVideoRef} muted playsInline />
            <p>วาง QR ของเพื่อนให้อยู่ตรงกลางภาพ</p>
          </div>
        </div>
      )}

      {adminScannerOpen && (
        <div className="reward-code-backdrop" role="dialog" aria-modal="true">
          <div className="reward-code-modal reward-admin-scanner">
            <button type="button" onClick={() => setAdminScannerOpen(false)}>×</button>
            <h2>สแกน QR เพื่อรับรางวัล</h2>
            <video ref={adminVideoRef} muted playsInline />
            <p>ใช้ได้ทั้ง QR คูปองและ QR โปรไฟล์สำหรับยืนยันเสื้อก๊วน</p>
          </div>
        </div>
      )}

      {referralInvite && (
        <div className="reward-code-backdrop" role="dialog" aria-modal="true">
          <div className="reward-code-modal reward-referral-modal">
            <button type="button" onClick={() => setReferralInvite(null)}>×</button>
            {referralInvite.mode === 'share' ? (
              <>
                <span>ชวนเพื่อนรับรางวัล</span>
                <h2>{referralInvite.title}</h2>
                <div className="reward-code-mark"><img src={referralQr} alt="QR ชวนเพื่อน" /></div>
                <strong>{referralInvite.rewardLabel || 'ฟรี 1 ชิป'}</strong>
                <p>ให้เพื่อนสแกน QR นี้ เข้าสู่ระบบ และกด Connect<br />เพื่อนแต่ละคนใช้สิทธิ์ได้เพียงครั้งเดียว</p>
                <button type="button" className="referral-copy" onClick={() => navigator.clipboard?.writeText(referralInvite.url)}>คัดลอกลิงก์ชวนเพื่อน</button>
              </>
            ) : (
              <>
                <span>WELCOME FRIEND</span>
                <h2>สวัสดีเพื่อนใหม่ 👋</h2>
                <p><strong>{referralInvite.inviter_name}</strong> ชวนคุณเข้าร่วม<br />{referralInvite.title}</p>
                <button type="button" className="referral-connect" onClick={connectReferral}>🤝 Connect กับ {referralInvite.inviter_name}</button>
                <small>หนึ่งบัญชีเข้าร่วมโปรโมชั่นนี้ได้เพียงครั้งเดียว</small>
              </>
            )}
          </div>
        </div>
      )}

      {redeem && (
        <div
          className="reward-code-backdrop"
          role="dialog"
          aria-modal="true"
        >
          <div className="reward-code-modal">

            <button
              type="button"
              onClick={() => setRedeem(null)}
            >
              ×
            </button>

            <span>
              สิทธิ์รอ Admin ยืนยัน
            </span>

            <h2>
              {redeem.title}
            </h2>

            <div className="reward-code-mark">
              {qrImage ? <img src={qrImage} alt="QR สำหรับยืนยันรับรางวัล" /> : 'กำลังสร้าง QR…'}
            </div>

            {redeem.rewardLabel && <strong>{redeem.rewardLabel}</strong>}

            <code>
              {redeem.redeemCode}
            </code>

            <p>
              แสดงรหัสนี้ให้ Admin ภายใน 5 นาที
              <br />
              รหัสใช้ได้ครั้งเดียว
            </p>

          </div>
        </div>
      )}

    </main>
  );
}

export default Reward;
